import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Tooltip,
    CircularProgress,
    Alert,
    Snackbar,
    Autocomplete
} from '@mui/material';
import {
    Edit,
    Delete,
    Visibility,
    Search,
    FilterList,
    PersonAdd,
    Email,
    Phone,
    ChevronLeft,
    ChevronRight,
    AccountCircle
} from '@mui/icons-material';
import { Layout } from '@layout/Layout';
import useEmployeesStore from '@store/EmployeesStore';
import { useAppTheme } from '@contexts/ThemeContext';
import { getAllEmployeesWithoutPagination } from '@services/HrService/EmployeesService';

// Type definitions
interface Employee {
    id: string;
    name: string;
    email: string;
    contact?: string;
    role_id?: string;
    role_name?: string;
    role_detail?: string;
    dept_id?: number;
    dept_name?: string;
    reporting_manager?: string | string[];
    reporting_manager_name?: string;
    reporting_managers?: Array<{ id: string; name: string; email: string }>;
    status?: 'active' | 'inactive';
    created_at?: string;
    is_deleted?: number;
    gender?: 'male' | 'female' | 'other' | 'prefer_not_to_say';
    date_of_birth?: string;
    hire_date?: string;
    experience_years?: number;
}

interface Department {
    id: number;
    dept_name: string;
}


const EmployeesPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { isDarkMode } = useAppTheme();
    const {
        employees,
        departments,
        loading,
        error,
        filters,
        pagination,
        currentPage,
        pageSize,
        fetchEmployees,
        fetchDepartments,
        addNewEmployee,
        updateEmployeeData,
        deleteEmployeeById,
        setFilters,
        clearFilters,
        setPage,
        clearSelectedEmployee,
        resetErrors
    } = useEmployeesStore();

    const [openDialog, setOpenDialog] = useState(false);
    const [dialogMode, setDialogMode] = useState<'add' | 'edit' | 'view'>('add');
    const [formData, setFormData] = useState<Partial<Employee>>({
        name: '',
        email: '',
        contact: '',
        role_id: '5bfacf62-a7f6-11f0-8c41-5eeb5bc49250',
        status: 'active',
        dept_id: undefined,
        reporting_manager: [],
        gender: undefined,
        date_of_birth: '',
        hire_date: '',
        experience_years: undefined
    });
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
        open: false,
        message: '',
        severity: 'success'
    });
    const [allEmployees, setAllEmployees] = useState<Employee[]>([]);
    const [loadingAllEmployees, setLoadingAllEmployees] = useState(false);

    // Fetch all employees for reporting manager dropdown
    const fetchAllEmployees = async () => {
        setLoadingAllEmployees(true);
        try {
            const result = await getAllEmployeesWithoutPagination({ status: 'active' });
            if (result.success) {
                setAllEmployees(result.data || []);
            }
        } catch (error) {
            console.error('Failed to fetch all employees:', error);
        } finally {
            setLoadingAllEmployees(false);
        }
    };

    // Fetch data on component mount
    useEffect(() => {
        fetchEmployees(currentPage, pageSize);
        fetchDepartments();
        fetchAllEmployees();
    }, []);

    // Handle edit from profile page navigation
    useEffect(() => {
        const editId = (location.state as { editId?: string })?.editId;
        if (editId && employees) {
            const employeeToEdit = employees.find((emp: Employee) => emp.id === editId);
            if (employeeToEdit) {
                handleOpenDialog('edit', employeeToEdit);
                // Clear the state to prevent re-opening on refresh
                navigate(location.pathname, { replace: true, state: {} });
            }
        }
    }, [location.state, employees]);

    const handleOpenDialog = (mode: 'add' | 'edit' | 'view', employee?: Employee) => {
        setDialogMode(mode);
        if (employee) {
            // Parse reporting_manager if it's a string (JSON array format)
            let reportingManagers: string[] = [];
            if (employee.reporting_manager) {
                if (typeof employee.reporting_manager === 'string') {
                    try {
                        reportingManagers = JSON.parse(employee.reporting_manager);
                    } catch (e) {
                        // If it's not valid JSON, treat it as a single ID
                        reportingManagers = [employee.reporting_manager];
                    }
                } else if (Array.isArray(employee.reporting_manager)) {
                    reportingManagers = employee.reporting_manager;
                }
            }
            setFormData({
                ...employee,
                reporting_manager: reportingManagers,
                // Format dates for input fields
                date_of_birth: employee.date_of_birth ? employee.date_of_birth.split('T')[0] : '',
                hire_date: employee.hire_date ? employee.hire_date.split('T')[0] : ''
            });
        } else {
            setFormData({
                name: '',
                email: '',
                contact: '',
                role_id: '5bfacf62-a7f6-11f0-8c41-5eeb5bc49250',
                status: 'active',
                dept_id: undefined,
                reporting_manager: [],
                gender: undefined,
                date_of_birth: '',
                hire_date: '',
                experience_years: undefined
            });
        }
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        clearSelectedEmployee();
        resetErrors();
    };

    const handleFormSubmit = async () => {
        try {
            // Prepare the data with reporting_manager as JSON string array
            const dataToSubmit = {
                ...formData,
                reporting_manager: Array.isArray(formData.reporting_manager) && formData.reporting_manager.length > 0
                    ? JSON.stringify(formData.reporting_manager)
                    : null
            };

            console.log('Form Data to Submit:', dataToSubmit);

            if (dialogMode === 'add') {
                await addNewEmployee(dataToSubmit);
                setSnackbar({
                    open: true,
                    message: 'Employee added successfully!',
                    severity: 'success'
                });
            } else if (dialogMode === 'edit' && formData.id) {
                await updateEmployeeData(formData.id, dataToSubmit);
                setSnackbar({
                    open: true,
                    message: 'Employee updated successfully!',
                    severity: 'success'
                });
            }
            handleCloseDialog();
        } catch (error) {
            console.error('Form submission error:', error);
            setSnackbar({
                open: true,
                message: `Failed to ${dialogMode === 'add' ? 'add' : 'update'} employee. Please try again.`,
                severity: 'error'
            });
        }
    };

    const handleDelete = async (id: string) => {
        if (window.confirm('Are you sure you want to delete this employee?')) {
            try {
                await deleteEmployeeById(id);
                setSnackbar({
                    open: true,
                    message: 'Employee deleted successfully!',
                    severity: 'success'
                });
            } catch (error) {
                console.error('Delete error:', error);
                setSnackbar({
                    open: true,
                    message: 'Failed to delete employee. Please try again.',
                    severity: 'error'
                });
            }
        }
    };

    const handleStatusChange = async (employeeId: string, newStatus: 'active' | 'inactive') => {
        try {
            await updateEmployeeData(employeeId, { status: newStatus });
            setSnackbar({
                open: true,
                message: `Employee ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully!`,
                severity: 'success'
            });
            // Refresh the employees list
            await fetchEmployees(currentPage, pageSize);
        } catch (error) {
            console.error('Status change error:', error);
            setSnackbar({
                open: true,
                message: 'Failed to update employee status. Please try again.',
                severity: 'error'
            });
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    // Helper function to get reporting manager names
    const getReportingManagerNames = (employee: Employee): string => {
        if (!employee.reporting_manager) return '-';

        let managerIds: string[] = [];
        if (typeof employee.reporting_manager === 'string') {
            try {
                managerIds = JSON.parse(employee.reporting_manager);
            } catch (e) {
                managerIds = [employee.reporting_manager];
            }
        } else if (Array.isArray(employee.reporting_manager)) {
            managerIds = employee.reporting_manager;
        }

        if (managerIds.length === 0) return '-';

        const managerNames = managerIds
            .map(id => {
                const manager = allEmployees.find(emp => emp.id === id);
                return manager ? manager.name : null;
            })
            .filter(name => name !== null);

        return managerNames.length > 0 ? managerNames.join(', ') : '-';
    };

    if (loading && !employees) {
        return (
            <Layout>
                <Container maxWidth="xl">
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
                        <CircularProgress size={60} />
                    </Box>
                </Container>
            </Layout>
        );
    }

    return (
        <Layout>
            <Container maxWidth="xl">
                <Box sx={{ p: 3 }}>
                    {/* Header */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Box>
                            <Typography variant="h4" component="h1" gutterBottom>
                                Employee Management
                            </Typography>
                            <Typography variant="subtitle1" color="textSecondary">
                                Manage your workforce efficiently
                            </Typography>
                        </Box>
                        <Button
                            variant="contained"
                            startIcon={<PersonAdd />}
                            onClick={() => handleOpenDialog('add')}
                            sx={{ py: 1.5, px: 3 }}
                        >
                            Add Employee
                        </Button>
                    </Box>

                    {error && (
                        <Alert severity="error" sx={{ mb: 3 }}>
                            {error}
                        </Alert>
                    )}

                    {/* Filters */}
                    <Card sx={{ mb: 3 }}>
                        <CardContent>
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
                                <TextField
                                    placeholder="Search employees..."
                                    value={filters.search}
                                    onChange={(e) => setFilters({ search: e.target.value })}
                                    InputProps={{
                                        startAdornment: <Search sx={{ mr: 1, color: 'action.active' }} />
                                    }}
                                    size="small"
                                />
                                <FormControl size="small">
                                    <InputLabel>Role</InputLabel>
                                    <Select
                                        value={filters.role || ''}
                                        label="Role"
                                        onChange={(e) => setFilters({ role: e.target.value })}
                                    >
                                        <MenuItem value="">All Roles</MenuItem>
                                        <MenuItem value="admin">Admin</MenuItem>
                                        <MenuItem value="employee">Employee</MenuItem>
                                        <MenuItem value="manager">Manager</MenuItem>
                                    </Select>
                                </FormControl>
                                <Button
                                    variant="outlined"
                                    startIcon={<FilterList />}
                                    onClick={clearFilters}
                                    size="small"
                                >
                                    Clear Filters
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Employees Table */}
                    <Card>
                        <CardContent sx={{ p: 0 }}>
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Name</TableCell>
                                            <TableCell>Contact</TableCell>
                                            <TableCell>Role</TableCell>
                                            <TableCell>Department</TableCell>
                                            <TableCell>Reporting Managers</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell align="center">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {employees && employees.map((employee: Employee) => (
                                            <TableRow key={employee.id} hover>
                                                <TableCell>
                                                    <Box>
                                                        <Typography variant="body2" fontWeight="medium">
                                                            {employee.name}
                                                        </Typography>
                                                        <Typography variant="caption" color="textSecondary">
                                                            {employee.email}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>{employee.contact || '-'}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={employee.role_name || 'No Role'}
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: isDarkMode ? 'rgba(33, 150, 243, 0.2)' : 'rgba(33, 150, 243, 0.1)',
                                                            color: isDarkMode ? '#90caf9' : '#1976d2',
                                                            border: isDarkMode ? '1px solid #90caf9' : '1px solid #1976d2',
                                                            fontWeight: 500
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {employee.role_name === 'Employee'
                                                        ? (employee.dept_name || '-')
                                                        : '--'
                                                    }
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                                        {getReportingManagerNames(employee)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <FormControl size="small" sx={{ minWidth: 120 }}>
                                                        <Select
                                                            value={employee.status || 'active'}
                                                            onChange={(e) => handleStatusChange(employee.id, e.target.value as 'active' | 'inactive')}
                                                            sx={{
                                                                '& .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: employee.status === 'active' ? '#4caf50' : '#f44336'
                                                                },
                                                                color: employee.status === 'active' ? '#4caf50' : '#f44336'
                                                            }}
                                                        >
                                                            <MenuItem value="active">Active</MenuItem>
                                                            <MenuItem value="inactive">Inactive</MenuItem>
                                                        </Select>
                                                    </FormControl>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                                        <Tooltip title="View Profile">
                                                            <IconButton
                                                                size="small"
                                                                color="primary"
                                                                onClick={() => navigate(`/employees/${employee.id}/profile`)}
                                                            >
                                                                <AccountCircle />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Quick View">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleOpenDialog('view', employee)}
                                                            >
                                                                <Visibility />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Edit">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => navigate(`/employees/${employee.id}/edit`)}
                                                            >
                                                                <Edit />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Delete">
                                                            <IconButton
                                                                size="small"
                                                                color="error"
                                                                onClick={() => handleDelete(employee.id)}
                                                            >
                                                                <Delete />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            {(!employees || employees.length === 0) && !loading && (
                                <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                                    <Typography>No employees found</Typography>
                                </Box>
                            )}
                            {pagination && pagination.totalPages > 1 && (
                                <Box sx={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    alignItems: 'center',
                                    p: 2,
                                    borderTop: '1px solid',
                                    borderColor: 'divider'
                                }}>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            startIcon={<ChevronLeft />}
                                            onClick={() => setPage(currentPage - 1)}
                                            disabled={!pagination.hasPreviousPage || loading}
                                        >
                                            Previous
                                        </Button>
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            px: 2,
                                            minWidth: 100,
                                            justifyContent: 'center'
                                        }}>
                                            <Typography variant="body2">
                                                Page {currentPage} of {pagination.totalPages}
                                            </Typography>
                                        </Box>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            endIcon={<ChevronRight />}
                                            onClick={() => setPage(currentPage + 1)}
                                            disabled={!pagination.hasNextPage || loading}
                                        >
                                            Next
                                        </Button>
                                    </Box>
                                </Box>
                            )}
                        </CardContent>
                    </Card>

                    {/* Employee Dialog */}
                    <Dialog
                        open={openDialog}
                        onClose={handleCloseDialog}
                        maxWidth="sm"
                        PaperProps={{
                            sx: {
                                height: '70vh',
                                maxHeight: '85vh',
                                width: '500px',
                                maxWidth: '90vw'
                            }
                        }}
                    >
                        <DialogTitle>
                            {dialogMode === 'add' ? 'Add New Employee' :
                             dialogMode === 'edit' ? 'Edit Employee' : 'Employee Details'}
                        </DialogTitle>
                        <DialogContent
                            sx={{
                                overflowY: 'auto',
                                '&::-webkit-scrollbar': {
                                    width: '8px'
                                },
                                '&::-webkit-scrollbar-track': {
                                    background: isDarkMode ? '#2a2a2a' : '#f1f1f1'
                                },
                                '&::-webkit-scrollbar-thumb': {
                                    background: isDarkMode ? '#555' : '#888',
                                    borderRadius: '4px'
                                },
                                '&::-webkit-scrollbar-thumb:hover': {
                                    background: isDarkMode ? '#777' : '#555'
                                }
                            }}
                        >
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 3,
                                mt: 1
                            }}>
                                <TextField
                                    fullWidth
                                    label="Full Name"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    disabled={dialogMode === 'view'}
                                    required
                                />
                                <TextField
                                    fullWidth
                                    label="Email"
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    disabled={dialogMode === 'view'}
                                    required
                                    InputProps={{
                                        startAdornment: <Email sx={{ mr: 1, color: 'action.active' }} />
                                    }}
                                />
                                <TextField
                                    fullWidth
                                    label="Contact Number"
                                    value={formData.contact || ''}
                                    onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
                                    disabled={dialogMode === 'view'}
                                    InputProps={{
                                        startAdornment: <Phone sx={{ mr: 1, color: 'action.active' }} />
                                    }}
                                />
                                <Autocomplete
                                    multiple
                                    fullWidth
                                    disabled={dialogMode === 'view'}
                                    options={allEmployees.filter((emp: Employee) => emp.id !== formData.id && emp.status === 'active')}
                                    getOptionLabel={(option) => option.name || ''}
                                    value={
                                        Array.isArray(formData.reporting_manager)
                                            ? allEmployees.filter((emp) => formData.reporting_manager?.includes(emp.id))
                                            : []
                                    }
                                    onChange={(event, newValue) => {
                                        const managerIds = newValue.map((emp) => emp.id);
                                        setFormData({ ...formData, reporting_manager: managerIds });
                                    }}
                                    loading={loadingAllEmployees}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Reporting Managers (Optional)"
                                            placeholder="Search by name..."
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {loadingAllEmployees ? <CircularProgress color="inherit" size={20} /> : null}
                                                        {params.InputProps.endAdornment}
                                                    </>
                                                ),
                                            }}
                                        />
                                    )}
                                    renderOption={(props, option) => (
                                        <li {...props} key={option.id}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                    {option.name}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                                                    {option.email}
                                                </Typography>
                                            </Box>
                                        </li>
                                    )}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    renderTags={(value, getTagProps) =>
                                        value.map((option, index) => (
                                            <Chip
                                                label={option.name}
                                                {...getTagProps({ index })}
                                                key={option.id}
                                                size="small"
                                            />
                                        ))
                                    }
                                />
                                <TextField
                                    fullWidth
                                    label="Role"
                                    value="Employee"
                                    disabled
                                    sx={{
                                        '& .MuiInputBase-input.Mui-disabled': {
                                            WebkitTextFillColor: '#1976d2',
                                            fontWeight: 500
                                        }
                                    }}
                                />
                                <FormControl fullWidth disabled={dialogMode === 'view'}>
                                    <InputLabel>Department</InputLabel>
                                    <Select
                                        value={formData.dept_id || ''}
                                        label="Department"
                                        onChange={(e) => setFormData({ ...formData, dept_id: e.target.value ? Number(e.target.value) : undefined })}
                                    >
                                        <MenuItem value="">None</MenuItem>
                                        {departments.map((dept: Department) => (
                                            <MenuItem key={dept.id} value={dept.id}>
                                                {dept.dept_name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <FormControl fullWidth disabled={dialogMode === 'view'}>
                                    <InputLabel>Gender</InputLabel>
                                    <Select
                                        value={formData.gender || ''}
                                        label="Gender"
                                        onChange={(e) => setFormData({ ...formData, gender: e.target.value as Employee['gender'] || undefined })}
                                    >
                                        <MenuItem value="">Not Specified</MenuItem>
                                        <MenuItem value="male">Male</MenuItem>
                                        <MenuItem value="female">Female</MenuItem>
                                        <MenuItem value="other">Other</MenuItem>
                                        <MenuItem value="prefer_not_to_say">Prefer not to say</MenuItem>
                                    </Select>
                                </FormControl>
                                <TextField
                                    fullWidth
                                    label="Date of Birth"
                                    type="date"
                                    value={formData.date_of_birth || ''}
                                    onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                                    disabled={dialogMode === 'view'}
                                    InputLabelProps={{ shrink: true }}
                                />
                                <TextField
                                    fullWidth
                                    label="Hire Date"
                                    type="date"
                                    value={formData.hire_date || ''}
                                    onChange={(e) => setFormData({ ...formData, hire_date: e.target.value })}
                                    disabled={dialogMode === 'view'}
                                    InputLabelProps={{ shrink: true }}
                                />
                                <TextField
                                    fullWidth
                                    label="Experience (Years)"
                                    type="number"
                                    value={formData.experience_years ?? ''}
                                    onChange={(e) => setFormData({ ...formData, experience_years: e.target.value ? parseFloat(e.target.value) : undefined })}
                                    disabled={dialogMode === 'view'}
                                    inputProps={{ step: 0.5, min: 0 }}
                                />
                                <FormControl fullWidth disabled={dialogMode === 'view'}>
                                    <InputLabel>Status</InputLabel>
                                    <Select
                                        value={formData.status || 'active'}
                                        label="Status"
                                        onChange={(e) => setFormData({ ...formData, status: e.target.value as 'active' | 'inactive' })}
                                    >
                                        <MenuItem value="active">Active</MenuItem>
                                        <MenuItem value="inactive">Inactive</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleCloseDialog}>
                                {dialogMode === 'view' ? 'Close' : 'Cancel'}
                            </Button>
                            {dialogMode !== 'view' && (
                                <Button
                                    onClick={handleFormSubmit}
                                    variant="contained"
                                    disabled={loading}
                                >
                                    {loading ? <CircularProgress size={20} /> : (dialogMode === 'add' ? 'Add Employee' : 'Update Employee')}
                                </Button>
                            )}
                        </DialogActions>
                    </Dialog>

                    {/* Success/Error Snackbar */}
                    <Snackbar
                        open={snackbar.open}
                        autoHideDuration={2000}
                        onClose={handleCloseSnackbar}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    >
                        <Alert
                            onClose={handleCloseSnackbar}
                            severity={snackbar.severity}
                            sx={{
                                width: '100%',
                                backgroundColor: snackbar.severity === 'success' ? '#4caf50' : '#f44336',
                                color: 'white',
                                '& .MuiAlert-icon': {
                                    color: 'white'
                                }
                            }}
                            variant="filled"
                        >
                            {snackbar.message}
                        </Alert>
                    </Snackbar>
                </Box>
            </Container>
        </Layout>
    );
};

export default EmployeesPage;
